import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/i18n.jsx';
import { useTheme } from '../hooks/useTheme.jsx';
import Container from '../components/UI/Container';
import Section from '../components/UI/Section';
import SectionTitle from '../components/UI/SectionTitle';
import HeroCard from '../components/UI/HeroCard.jsx';
import EnhancedHero from '../components/UI/EnhancedHero.jsx';
import Partners from '../components/UI/Partners.jsx';

// Hero Section (Enhanced)
const Hero = () => {
  return <EnhancedHero />;
};

// Services Overview Section (Enhanced)
const ServicesOverview = () => {
  const { t } = useI18n();

  const services = [
    {
      key: 'foreignPlacement',
      icon: '🎓'
    },
    {
      key: 'languageCourses',
      icon: '🌐'
    },
    {
      key: 'visaDocuments',
      icon: '📄'
    },
    {
      key: 'flightAccommodation',
      icon: '✈️'
    },
    {
      key: 'studentConsulting',
      icon: '💡'
    }
  ];

  return (
    <Section className="bg-neutral-light dark:bg-neutral-900/50">
      <Container>
        <SectionTitle
          title={t('services.title')}
          subtitle={t('services.subtitle')}
        />
        
        <div className="mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6">
          {services.map((service, index) => (
            <HeroCard
              key={service.key}
              icon={service.icon}
              title={t(`services.${service.key}.title`)}
              description={t(`services.${service.key}.description`)}
              delay={index * 100}
            />
          ))}
        </div>

        <div className="mt-12 text-center animate-fade-in-up delay-400">
          <Link
            to="/services"
            className="group inline-flex items-center gap-3 px-6 py-3 bg-white dark:bg-neutral-800 rounded-full border-2 border-primary dark:border-accent text-primary dark:text-white font-semibold hover-lift overflow-hidden"
          >
            <span>{t('services.viewAll')}</span>
            <svg
              className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {/* Why Partner Section */}
        <div className="mt-16 md:mt-20 max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-primary dark:text-white mb-8 md:mb-12 text-center animate-fade-in-up">
            {t('services.whyPartner.title')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              { key: 'official', icon: '📋' },
              { key: 'fullService', icon: '📦' },
              { key: 'trusted', icon: '🤝' },
              { key: 'professional', icon: '👥' },
              { key: 'directPartnership', icon: '🔗' },
              { key: 'exclusive', icon: '⭐' },
              { key: 'humanitarian', icon: '🌟' }
            ].map((item, index) => (
              <div
                key={item.key}
                className="p-6 md:p-8 bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700 hover:border-accent dark:hover:border-accent transition-all duration-300 hover:shadow-xl animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="text-3xl md:text-4xl shrink-0">{item.icon}</div>
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-primary dark:text-white mb-2">
                      {t(`services.whyPartner.${item.key}.title`)}
                    </h3>
                    <p className="text-sm md:text-base text-neutral-600 dark:text-neutral-300 leading-relaxed">
                      {t(`services.whyPartner.${item.key}.description`)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
};

// Call to Action Section (Enhanced)
const CallToAction = () => {
  const { t } = useI18n();

  return (
    <Section className="bg-gradient-to-br from-primary via-primary-light to-primary-dark text-white overflow-hidden relative pb-24 md:pb-16">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-4 left-4 md:top-10 md:left-10 w-16 h-16 md:w-32 md:h-32 rounded-full border-2 border-white/10 animate-rotate-360" />
        <div className="absolute bottom-4 right-4 md:bottom-10 md:right-10 w-20 h-20 md:w-48 md:h-48 rounded-full border-2 border-white/10 animate-rotate-360" style={{ animationDirection: 'reverse' }} />
        <div className="absolute inset-0 bg-white/5 backdrop-blur-[1px]" />
      </div>

      <Container className="relative z-10">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-3 md:mb-4 animate-fade-in-up text-shimmer">
            {t('cta.title')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 md:mb-10 text-blue-100 animate-fade-in-up delay-200">
            {t('cta.subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 animate-fade-in-up delay-400">
            <Link
              to="/contact"
              className="group relative px-6 py-3 md:px-8 md:py-4 bg-white text-primary font-bold rounded-xl overflow-hidden hover-lift shine-on-hover shadow-lg md:shadow-2xl text-sm md:text-base"
            >
              <span className="relative z-10">{t('hero.cta')}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-accent to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>
            
            <Link
              to="/about"
              className="group relative px-6 py-3 md:px-8 md:py-4 bg-transparent border-2 border-white text-white font-semibold rounded-xl overflow-hidden hover-lift text-sm md:text-base"
            >
              <span className="relative z-10 group-hover:text-accent transition-colors duration-300">
                {t('cta.learnMore')}
              </span>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>
          </div>

          {/* Success Indicators - Responsive */}
          <div className="mt-8 md:mt-12 grid grid-cols-3 gap-3 md:gap-6 animate-fade-in-up delay-600">
            {[
              { key: 'students' },
              { key: 'experience' },
              { key: 'successRate' }
            ].map((stat, index) => (
              <div key={index} className="glass-dark rounded-xl p-3 md:p-4 hover-lift">
                <div className="text-xl md:text-2xl font-bold text-accent">{t(`stats.${stat.key}.value`)}</div>
                <div className="text-xs md:text-sm text-blue-100">{t(`stats.${stat.key}.label`)}</div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
};

// Main Home Component
const Home = () => {
  return (
    <main>
      <Hero />
      <Partners />
      <ServicesOverview />
      <CallToAction />
      {/* Bottom padding for mobile nav */}
      <div className="pb-24 md:pb-0"></div>
    </main>
  );
};

export default Home;
